import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { TMentionUser } from '@onesource/schemas';
import { getMediaUrl, useSearchMentionUsersQuery } from '@onesource/services';
import { FC, useEffect, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import { useBlockUserConfirm } from 'src/hooks/use-block-user-confirm';

const MIN_QUERY_LENGTH = 2;

type TProps = {
  /** True while a block is in flight — the input shows a spinner and is locked. */
  disabled?: boolean;
};

/** Member search that adds the picked member to the block list (after confirmation). */
export const AccountBlockedUserSearch: FC<TProps> = ({ disabled }) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const confirmBlockUser = useBlockUserConfirm();

  // Debounce the typed query (300ms) before it drives the server search.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const hasEnoughToSearch = debouncedQuery.length >= MIN_QUERY_LENGTH;
  const { data: users, isFetching } = useSearchMentionUsersQuery(debouncedQuery, {
    enabled: hasEnoughToSearch,
    // You can't block yourself, so never offer yourself as a result.
    excludeSelf: true,
  });

  const options: TMentionUser[] = hasEnoughToSearch ? (users ?? []) : [];
  const isSearching = hasEnoughToSearch && isFetching;
  const showSpinner = isSearching || disabled;

  const handleSelect = (user: TMentionUser | null) => {
    if (!user) {
      return;
    }
    confirmBlockUser({ mentionId: user.id }, user.displayName);
    setQuery('');
  };

  return (
    <Autocomplete
      size="small"
      options={options}
      loading={isSearching}
      disabled={disabled}
      value={null}
      inputValue={query}
      onInputChange={(_, newValue, reason) => reason !== 'reset' && setQuery(newValue)}
      onChange={(_, user) => handleSelect(user)}
      getOptionLabel={o => o.displayName}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      filterOptions={x => x} // options are already server-filtered
      noOptionsText={query.trim().length < MIN_QUERY_LENGTH ? 'Type to search members' : 'No members found'}
      forcePopupIcon={false}
      sx={{ maxWidth: 480 }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={key} {...optionProps} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar src={getMediaUrl(option.avatar ?? undefined)} sx={{ width: 28, height: 28 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {option.displayName}
              </Typography>
              {option.username && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  @{option.username}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={disabled ? 'Blocking…' : 'Search members to block…'}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <MdSearch size={18} />
              </InputAdornment>
            ),
            endAdornment: showSpinner ? <CircularProgress color="inherit" size={16} /> : null,
          }}
        />
      )}
    />
  );
};
